import React, { useEffect, useState } from 'react';
import { ChatContainer } from './ChatContainer';
import { ChatHeader } from './ChatHeader';
import { MessageBubble } from './MessageBubble';
import { MessageInput } from './MessageInput';
import { TypingIndicator } from './TypingIndicator';
import { useThemeStyles } from '../../contexts/ChatThemeContext';

interface Message {
  id: string;
  content: string;
  timestamp: Date;
  sender: 'user' | 'other';
  senderName?: string;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  reactions?: Array<{ emoji: string; count: number; hasReacted: boolean }>;
}

interface ThemedChatInterfaceProps {
  messages: Message[];
  currentUser: {
    name: string;
    initials: string;
  };
  otherUser: {
    name: string;
    initials: string;
    isOnline?: boolean;
  };
  onSendMessage: (message: string) => void;
  isTyping?: boolean;
  typingUser?: string;
  onTyping?: () => void;
  onStopTyping?: () => void;
  onReply?: (messageId: string) => void;
  onReact?: (messageId: string, emoji: string) => void;
  onAttach?: () => void;
  onEmojiClick?: () => void;
  disabled?: boolean;
}

export const ThemedChatInterface: React.FC<ThemedChatInterfaceProps> = ({
  messages,
  currentUser,
  otherUser,
  onSendMessage,
  isTyping = false,
  typingUser,
  onTyping,
  onStopTyping,
  onReply,
  onReact,
  onAttach,
  onEmojiClick,
  disabled = false,
}) => {
  const styles = useThemeStyles();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  return (
    <ChatContainer>
      <ChatHeader
        userName={otherUser.name}
        userInitials={otherUser.initials}
        isOnline={otherUser.isOnline}
      />
      
      <div 
        className="chat-messages"
        style={styles.messageArea}
      >
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            showSenderName={message.sender === 'other' && message.senderName !== undefined}
            onReply={() => onReply?.(message.id)}
            onReact={(emoji) => onReact?.(message.id, emoji)}
            reactions={message.reactions}
          />
        ))}
        
        {isTyping && (
          <TypingIndicator userName={typingUser} />
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <MessageInput
        onSendMessage={onSendMessage}
        onTyping={onTyping}
        onStopTyping={onStopTyping}
        disabled={disabled}
        onAttach={onAttach}
        onEmojiClick={onEmojiClick}
      />
    </ChatContainer>
  );
};